import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import styled, { css } from 'styled-components';

import { getBrandingColor, colorTypes } from 'src/branding';

const smallBigScreenBreakpoint = '460px';
const ResultList = styled.ul`
  position: relative;
  max-height: ${({ bigScreenHeight }) => (bigScreenHeight ? bigScreenHeight : '600px')};
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  margin-top: 1px;
  border: 1px solid #e4e4e4;
  z-index: 3;

  width: 100%;
  @media screen and (max-width: ${smallBigScreenBreakpoint}) {
    max-height: ${({ smallScreenHeight }) => (smallScreenHeight ? smallScreenHeight : '300px')};
  }
`;
const ResultItem = styled.li`
  list-style-type: none;
  &:hover {
    cursor: pointer;
  }

  ${({ selected }) => (selected
  ? css`
          background-color: blue;
        `
  : '')}
`;

export const UpDownArrow = styled.span`
  font-size: 2em;
  position: absolute;
  right: 9px;
  top: 18px;
  color: ${getBrandingColor(colorTypes.primary)};
  :hover {
    cursor: pointer;
  }

  ${({ down }) => (down
  ? css`
          width: 0;
          height: 0;
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;

          border-bottom: 10px solid ${getBrandingColor(colorTypes.primary)};
        `
  : css`
          width: 0;
          height: 0;
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;

          border-top: 10px solid ${getBrandingColor(colorTypes.primary)};
        `)}
`;

const InputWrapper = styled.div`
  @media screen and (max-width: ${smallBigScreenBreakpoint}) {
    input {
      font-size: 1rem;
    }
    ${UpDownArrow} {
      top: 13px;
      right: 4px;
    }
  }
  position: relative;
`;

const Input = styled.input`
  max-width: 100%;
  box-sizing: border-box;
  font-size: 1.1rem;
  border: 2px solid ${getBrandingColor(colorTypes.primary)};
  border-radius: 4px;
  color: ${({ dropdownCollapsed }) => (dropdownCollapsed ? getBrandingColor(colorTypes.primary) : 'black')};
  padding: 8px;
  width: 100%;
`;

const MultiselectSelectedInfo = styled.div`
  display: flex;
  flex-direction: row;
`;

const BottomPart = styled.div`
  padding: 10px;
  border: 1px solid gray;
  background: white;
  position: relative;

  width: ${({ bigScreenWidth }) => (bigScreenWidth ? bigScreenWidth : '430px')};
  z-index: 1;
  box-sizing: border-box;

  @media screen and (max-width: ${smallBigScreenBreakpoint}) {
    width: ${({ smallScreenwidth }) => (smallScreenwidth ? smallScreenwidth : '90%')};
  }
`;

const CloseMultiselectBtn = styled.button`
  width: ${({ bigScreenWidth }) => (bigScreenWidth ? `calc(${bigScreenWidth} - 20px)` : '430px')};
  margin-left: 0px;
  font-size: 1rem;
  font-weight: 600;
  padding: 10px;
  background: none;
  border: 1px solid #b3b3b3;
  color: black;
  border-radius: 4px;
  margin-top: 12px;
  :hover {
    cursor: pointer;
  }
  &:active{
    color: inherit;
  }
  @media screen and (max-width: ${smallBigScreenBreakpoint}) {
    width: ${({ smallScreenwidth }) => (smallScreenwidth ? smallScreenwidth : '90%')};
  }
`;

const ShowSelectedFilterMultiselectBtn = styled(CloseMultiselectBtn)``;

export const Wrapper = styled.div`
  ${InputWrapper}, ${MultiselectSelectedInfo} {
    width: ${({ bigScreenWidth }) => (bigScreenWidth ? bigScreenWidth : '430px')};

    @media screen and (max-width: ${smallBigScreenBreakpoint}) {
      width: ${({ smallScreenwidth }) => (smallScreenwidth ? smallScreenwidth : '90%')};
      max-width: 90vw;
    }
  }
`;

export const SearchableDropdown = ({
  // the items to choose from in the dropdown
  dataitems,

  // is multiselected enabled or not
  multiSelect,

  // --- single-select only ---
  // from dataitems, which property should be used for searching
  itemSearchKey,
  selectedItem, // which items in dataitems is selected
  initialValue, // displayed when the dropdown is closed and when no item is selected
  onSelectedChange, // the user choosed an item

  // --- multi items only ---
  selectMultipleSelectedIdentifiersChanged, // when an item is selcted
  onMultiselectChoosedItems, // when the user clicks OK
  selectedMultipleItemsIdentifiers, // the selected items
  multiSelectIdentifierKey, // itemSearchKey but for multi-select
  // As initial display value when menu is closed, we will display "Trykk for å velge x" where x is
  // this prop
  multiSelectItemName,

  // displayed in the inputfield when the dropdown is open and contains no value
  placeholder,

  renderItem, // how shuld each element in the dropdown look

  bigScreenWidth,
  smallScreenwidth,
  bigScreenHeight,
  smallScreenHeight,
  dropdownStyle,

  disabled,
  preventCloseOnFocusLoss,
  renderSelectedItemInInput: getSelectedItemDisplay, // a function to get the text-content to display

  onShowSelectedModeChange, // when toe user clicks to show only selected items
  onClose,
}) => {
  if (!getSelectedItemDisplay) {
    console.error(
      'Missing handler for renderSelectgedItemInInput in searchable dropdown',
    );
  }

  if (!renderItem) {
    console.error('Missing handler for renderItem in searchable dropdown');
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [onlyShowSelected, setOnlyShowSelected] = useState(false);

  const [focusedItemIdx, setFocusedItemIdx] = useState(0);
  const [displayResultList, setDisplayResultList] = useState(false);

  const resultListRef = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const caretToggleRef = useRef(null);

  const onCloseDropdown = useCallback(() => {
    inputRef.current.blur();
    if (onClose) {
      onClose();
    }
    setDisplayResultList(false);
  }, []);

  useEffect(() => {
    if (preventCloseOnFocusLoss) {
      return;
    }

    const click = (evt) => {
      if (
        resultListRef
        && resultListRef.current
        && caretToggleRef
        && caretToggleRef.current
        && !caretToggleRef.current.contains(evt.target)
        && !resultListRef.current.contains(evt.target)
      ) {
        onCloseDropdown();
      }
    };

    document.addEventListener('mousedown', click);
    document.addEventListener('touchstart', click);

    return () => {
      document.removeEventListener('mosuedown', click);
      document.removeEventListener('touchdown', click);
    };
  }, [onCloseDropdown, preventCloseOnFocusLoss]);

  const handleKeypress = (evt) => {
    const arrowUp = 38;
    const arrowDown = 40;
    const enter = 13;
    const escape = 27;

    if (evt.keyCode === arrowDown) {
      const newSelectedIndex = Math.min(
        dataitems.length - 1,
        focusedItemIdx + 1,
      );
      const newSelectedItem = resultListRef.current.children[newSelectedIndex];
      setFocusedItemIdx(newSelectedIndex);

      // scroll so that the selcted item is jsut visible
      resultListRef.current.scrollTop = newSelectedItem.offsetTop
        - resultListRef.current.clientHeight
        + newSelectedItem.clientHeight;
    } else if (evt.keyCode === arrowUp) {
      const newSelectedIndex = Math.max(0, focusedItemIdx - 1);
      const newSelectedItem = resultListRef.current.children[newSelectedIndex];
      setFocusedItemIdx(newSelectedIndex);

      resultListRef.current.scrollTop = newSelectedItem.offsetTop
        - resultListRef.current.clientHeight
        + newSelectedItem.clientHeight;
    } else if (evt.keyCode === enter) {

    } else if (evt.keyCode === escape) {
      setDisplayResultList(false);
      if (onClose) {
        onClose();
      }

      // blurring will also hide the list, but explicitly hiding the list is faster
      inputRef.current.blur();
    }
  };

  let valueToDisplayInInputField = '';

  if (multiSelect) {
    if (displayResultList) {
      valueToDisplayInInputField = searchTerm;
    } else {
      valueToDisplayInInputField = selectedMultipleItemsIdentifiers.length
        ? `${selectedMultipleItemsIdentifiers.length} ${multiSelectItemName} valgt. Trykk for å endre`
        : `Trykk for å velge ${multiSelectItemName}`;
    }
  } else {
    if (initialValue && selectedItem === null && !displayResultList) {
      valueToDisplayInInputField = initialValue;
    } else if (displayResultList) {
      valueToDisplayInInputField = searchTerm;
    } else {
      valueToDisplayInInputField = getSelectedItemDisplay(selectedItem);
    }
  }

  const getItemsMatchingSearchQueryAndFiltering = () => {
    const matching = dataitems.filter(
      (item) => item[itemSearchKey]
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    );
    if (onlyShowSelected) {
      return matching.filter(
        (item) => selectedMultipleItemsIdentifiers.indexOf(
          item[multiSelectIdentifierKey],
        ) >= 0,
      );
    } else {
      return matching;
    }
  };

  return (
    <Wrapper
      ref={wrapperRef}
      smallScreenwidth={smallScreenwidth}
      bigScreenWidth={bigScreenWidth}
    >
      <InputWrapper>
        <Input
          disabled={disabled}
          placeholder={
            displayResultList || selectedItem === null ? placeholder : ''
          }
          dropdownCollapsed={!displayResultList}
          ref={inputRef}
          onClick={() => {
            if (disabled) {
              return;
            }
            inputRef.current.select();
          }}
          value={valueToDisplayInInputField}
          onChange={(evt) => {
            setSearchTerm(evt.target.value);
            setFocusedItemIdx(0);
          }}
          onFocus={() => {
            if (disabled) {
              return;
            }
            setDisplayResultList(true);
            if (!multiSelect) {
              onSelectedChange(null);
            }
          }}
          onKeyDown={handleKeypress}
        />

        <UpDownArrow
          ref={caretToggleRef}
          onClick={() => {
            if (displayResultList) {
              setDisplayResultList(false);
              inputRef.current.blur();
              if (multiSelect) {
                setOnlyShowSelected(false);
                setDisplayResultList(false);
                onShowSelectedModeChange('ALL');
                onMultiselectChoosedItems(
                  selectedMultipleItemsIdentifiers.map((e) => dataitems.find((d) => d[multiSelectIdentifierKey] === e)),
                );
              }
              if (onClose) {
                onClose();
              }
            } else {
              setDisplayResultList(true);
              inputRef.current.focus();
            }
          }}
          aria-hidden="true"
          down={displayResultList}
        />
      </InputWrapper>
      <BottomPart
        bigScreenWidth={bigScreenWidth}
        smallScreenwidth={smallScreenwidth}
        style={{ display: displayResultList ? 'block' : 'none', ...dropdownStyle || {} }}
      >
        {displayResultList && (
          <>
            <ResultList
              ref={resultListRef}
              bigScreenHeight={bigScreenHeight}
              smallScreenHeight={smallScreenHeight}
            >
              {getItemsMatchingSearchQueryAndFiltering().map((item, index) => (
                <ResultItem
                  key={index}
                  onClick={() => {
                    if (
                      multiSelect
                    && selectedMultipleItemsIdentifiers.indexOf(
                      item[multiSelectIdentifierKey],
                    ) === -1
                    ) {
                      selectMultipleSelectedIdentifiersChanged(
                        selectedMultipleItemsIdentifiers.concat([
                          item[multiSelectIdentifierKey],
                        ]),
                      );
                    }

                    if (
                      multiSelect
                    && selectedMultipleItemsIdentifiers.indexOf(
                      item[multiSelectIdentifierKey],
                    ) >= 0
                    ) {
                      selectMultipleSelectedIdentifiersChanged(
                        selectedMultipleItemsIdentifiers.filter(
                          (e) => e !== item[multiSelectIdentifierKey],
                        ),
                      );
                    } else if (!multiSelect) {
                      onSelectedChange(item);
                      if (onClose) {
                        onClose();
                      }
                      setDisplayResultList(false);
                    }
                  }}
                  onMouseOver={() => setFocusedItemIdx(index)}
                >
                  {renderItem(
                    item,
                    searchTerm,
                    index === focusedItemIdx,
                    multiSelect
                    && selectedMultipleItemsIdentifiers.indexOf(
                      item[multiSelectIdentifierKey],
                    ) >= 0,
                  )}
                </ResultItem>
              ))}
            </ResultList>
          </>
        )}
        {displayResultList && multiSelect && (
          <div>
            <ShowSelectedFilterMultiselectBtn
              smallScreenwidth={smallScreenwidth}
              bigScreenWidth={bigScreenWidth}
              onClick={() => {
                if (onShowSelectedModeChange) {
                  onShowSelectedModeChange(onlyShowSelected ? 'ALL' : 'SELECTED');
                }
                setOnlyShowSelected((old) => !old);
              }}
            >
              {onlyShowSelected ? 'Vis alle' : 'Vis bare valgte'}
            </ShowSelectedFilterMultiselectBtn>
            <CloseMultiselectBtn
              smallScreenwidth={smallScreenwidth}
              bigScreenWidth={bigScreenWidth}
              onClick={() => {
                setOnlyShowSelected(false);
                setDisplayResultList(false);
                onShowSelectedModeChange('ALL');

                onMultiselectChoosedItems(
                  selectedMultipleItemsIdentifiers.map((e) => dataitems.find((d) => d[multiSelectIdentifierKey] === e)),
                );

                if (onClose) {
                  onClose();
                }
              }}
            >
              Ferdig
            </CloseMultiselectBtn>
          </div>
        )}
      </BottomPart>
    </Wrapper>
  );
};

export const RenderPartHightlightMatch = ({
  part,
  searchTerm,
  contentWrapperStyle,
  contentStyle,
  highlightedPartStyle,
  wrapText,
  disableHighlightMatchOnSearch,
}) => {
  let choosenPart = [];
  const parts = searchTerm
    ? part.split(new RegExp(`(${searchTerm})`, 'i'))
    : [part];

  if (!disableHighlightMatchOnSearch) {
    const parts1 = searchTerm
      ? part.split(
        new RegExp(
          `(${searchTerm.slice(0, 1)}.${searchTerm.slice(1)})`,
        ),
      )
      : [part];
    const parts2 = searchTerm
      ? part.split(
        new RegExp(
          `(${searchTerm.slice(0, 2)}.${searchTerm.slice(2)})`,
        ),
      )
      : [part];

    if (parts2.length > parts1.length && parts2.length > parts.length) {
      choosenPart = parts2;
    } else if (parts1.length > parts2.length && parts1.length > parts.length) {
      choosenPart = parts1;
    } else {
      choosenPart = parts;
    }
  } else {
    choosenPart = [part];
  }

  return (
    <span
      key={part}
      style={{
        display: 'inline-block',
        flexBasis: '100%',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        ...contentWrapperStyle,
        ...(wrapText ? { whiteSpace: 'normal', textAlign: 'left', wordBreak: 'break-word' } : {}),
      }}
    >
      {choosenPart.map((item, idx) => {
        if (
          !disableHighlightMatchOnSearch && (
            item === searchTerm
          || (item.replace(/\./g, '') === searchTerm.replace(/\./g, ''))
          || (searchTerm.length > 0 && `${searchTerm}.` === item))
        ) {
          return (
            <React.Fragment key={idx}>
              <span
                key={idx}
                style={{
                  textShadow: '1px 0px 0px black', letterSpacing: '0', ...contentStyle, ...highlightedPartStyle,
                }}
              >
                {item}
              </span>
            </React.Fragment>
          );
        } else {
          return (
            <span
              key={idx}
              style={{ ...contentStyle }}
            >
              {item}
            </span>
          );
        }
      })}
    </span>
  );
};

export const formatMoney = (amount) => `${amount.slice(0, 1)}.${amount.slice(1)} kr`;

export const Separator = styled.div`
  flex-basis: 100%;
  text-align: center;
`;

export const DropdownItem = styled.div`
  border: 0.5px solid #e1e1e1;
  position: relative;
  display: flex;
  width: 100%;
  flex: 1 1 0px;
  background-color: ${({ itemHasFocus }) => (itemHasFocus ? '#f0f0f0' : 'white')};
  padding: 8px;
  font-size: ${({ fontSize }) => (fontSize ? fontSize : '1.4rem')};

  ${({ itemIsChoosed }) => (itemIsChoosed
  ? css`
          background-color: #dceaff;
        `
  : '')}

  @media screen and (max-width: 450px) {
    width: 94%;
  }

  @media screen and (max-width: 400px) {
    font-size: ${({ smallFontOnSmallScreens }) => (smallFontOnSmallScreens ? '1.4rem' : '1.3rem !important')};
  }

`;
